import { REASONING_RECOVERY_KEY_PATTERN } from "~/api/reasoningTypes";

/**
 * The reasoning command journals (`reasoning-request`, `criterion-review`,
 * `reasoning-run-action`, `reasoning-artifact`) persist to browser storage BEFORE a command is dispatched so a lost
 * response can be replayed with the same command id. Storage can be disabled, full or
 * hostile: every read answers null and every write is swallowed — the command still runs, it
 * only loses its replay. The journals mirror to the VS Code host when rendered in its webview.
 */
export const REASONING_RECOVERY_CHANGE = "docit:reasoning-recovery-change";
/** When each journal entry was last written (ms), so abandoned ones can be pruned by age. */
const INDEX_KEY = "docit:reasoning-recovery-index:1";
export const DEFAULT_RECOVERY_MAX_AGE_DAYS = 14;

export const reasoningRequestKey = (workspaceId: string, userId: string) =>
  `docit:reasoning-request:2:${workspaceId}:${userId}`;
export const criterionReviewKey = (sessionId: string) => `docit:criterion-review:1:${sessionId}`;
export const reasoningRunActionKey = (runId: string) => `docit:reasoning-run-action:1:${runId}`;

export function isReasoningRecoveryKey(key: string): boolean {
  return REASONING_RECOVERY_KEY_PATTERN.test(key);
}

function readIndex(): Record<string, number> {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, number>)
      : {};
  } catch {
    return {};
  }
}
function writeIndex(index: Record<string, number>): void {
  try {
    if (Object.keys(index).length) localStorage.setItem(INDEX_KEY, JSON.stringify(index));
    else localStorage.removeItem(INDEX_KEY);
  } catch {
    /* The index is a convenience for pruning; losing it costs nothing but a later prune. */
  }
}
function announce(key: string, value: string | null): void {
  try {
    window.dispatchEvent(new CustomEvent(REASONING_RECOVERY_CHANGE, { detail: { key, value } }));
  } catch {
    /* No window (or a frozen one): the host mirror simply does not learn of this write. */
  }
}

export function readReasoningRecovery(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function saveReasoningRecovery(key: string, value: string, now = Date.now()): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    return; // Nothing was stored; announcing it would mirror a value the browser does not hold.
  }
  const index = readIndex();
  index[key] = now;
  writeIndex(index);
  announce(key, value);
}

export function clearReasoningRecovery(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* Already unreadable; fall through so the mirror forgets it too. */
  }
  const index = readIndex();
  if (key in index) {
    delete index[key];
    writeIndex(index);
  }
  announce(key, null);
}

/**
 * Drops journal entries older than `maxAgeDays`. An entry written before the index existed is
 * stamped now and judged on the next prune. Returns the keys it removed.
 */
export function pruneReasoningRecovery(
  maxAgeDays = DEFAULT_RECOVERY_MAX_AGE_DAYS,
  now = Date.now(),
): string[] {
  const removed: string[] = [];
  let keys: string[];
  try {
    keys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i)).filter(
      (key): key is string => !!key && isReasoningRecoveryKey(key),
    );
  } catch {
    return removed;
  }
  const index = readIndex();
  const cutoff = now - maxAgeDays * 24 * 60 * 60 * 1000;
  for (const key of keys) {
    const savedAt = index[key];
    if (savedAt === undefined) index[key] = now;
    else if (savedAt < cutoff) {
      try {
        localStorage.removeItem(key);
      } catch {
        continue;
      }
      delete index[key];
      removed.push(key);
      announce(key, null);
    }
  }
  for (const key of Object.keys(index))
    if (!keys.includes(key) && !removed.includes(key)) delete index[key];
  writeIndex(index);
  return removed;
}
